import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import EventService from '../../services/EventService';

const MONTH_FILTER_OPTIONS = [
  { label: '3 Months', value: 3 },
  { label: '6 Months', value: 6 },
  { label: '12 Months', value: 12 },
];

const DEFAULT_MONTHS = MONTH_FILTER_OPTIONS[0].value;
const CHART_HEIGHT = 300;

const formatRevenue = (value) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatEventCount = (value) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatTooltipValue = (value, name) => {
  if (name === 'Total Revenue') {
    return [formatRevenue(value), name];
  }

  return [formatEventCount(value), name];
};

const GrowthBoard = () => {
  const [selectedMonths, setSelectedMonths] = useState(DEFAULT_MONTHS);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const loadRevenueChart = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');

        const revenueRows = await EventService.getEventRevenueChart(
          selectedMonths,
          { signal: controller.signal },
        );

        setChartData(revenueRows);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return;
        }

        console.error('Failed to load event revenue chart:', error);
        setChartData([]);
        setErrorMessage('Unable to load revenue data right now.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void loadRevenueChart();

    return () => {
      controller.abort();
    };
  }, [selectedMonths]);

  const hasChartData = chartData.length > 0;

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h6 className="mb-1 fw-bold">Event Revenue Growth</h6>
          <p className="mb-0 text-sm text-slate-500">
            Monthly view of total revenue and event count.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span>Range</span>
          <select
            value={selectedMonths}
            onChange={(event) => setSelectedMonths(Number(event.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 focus:border-sky-400"
            aria-label="Select revenue chart month range"
          >
            {MONTH_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3"
        style={{ minHeight: `${CHART_HEIGHT}px` }}
      >
        {isLoading ? (
          <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-500">
            Loading revenue chart...
          </div>
        ) : errorMessage ? (
          <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-rose-500">
            {errorMessage}
          </div>
        ) : !hasChartData ? (
          <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-500">
            No revenue data available for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <XAxis dataKey="month" tickLine={false} axisLine={false} />
              <YAxis
                yAxisId="count"
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickFormatter={formatEventCount}
              />
              <YAxis
                yAxisId="revenue"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickFormatter={formatRevenue}
              />
              <Tooltip
                formatter={formatTooltipValue}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend />
              <Bar
                yAxisId="count"
                dataKey="eventCount"
                name="Event Count"
                fill="#D07C3F"
                radius={[8, 8, 0, 0]}
              />
              <Bar
                yAxisId="revenue"
                dataKey="revenue"
                name="Total Revenue"
                fill="#434343"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default GrowthBoard;
